/**
 * Which paper the Light / Graphite / Black switch lands on (§10, screen
 * `41-profile-panel`; three-way since Nath's ruling of 3 Oct 2026).
 *
 * Framework-free on purpose, for the same reason `presence-tiles.ts` is: this
 * is the only part of the appearance row with a wrong answer, so it is
 * testable without a TestBed.
 *
 * THE BUG THIS EXISTS TO CLOSE. The profile panel offers a TWO-WAY switch
 * over a NINE-paper catalogue. It used to call `ThemeService.set('light' |
 * 'dark')`, which hard-maps to `workplace-paper` / `workplace-slate`. So a
 * viewer on `high-contrast-light` who tapped Dark and then Light again came
 * back on Cream Wove: their accessibility paper silently discarded, by a
 * control that never claimed to change anything but luminance, with nothing
 * on this surface able to give it back.
 *
 * WHAT IS AND IS NOT INVENTED HERE. `RICOH_PAPER_THEMES` carries no pairing
 * metadata — no `pairsWith`, no family — so a table mapping Manila to
 * Blueprint or Bleached to Carbon would be a guess dressed as a preference.
 * The only pair the catalogue itself names is high contrast, whose two
 * entries are one accessibility choice spelled at two luminances. That pair
 * is honoured; nothing else is fabricated. Everything else relies on a paper
 * this session has actually SEEN the viewer on, and falls back to the mode
 * default only when there is genuinely nothing to remember.
 */

import type { ThemeName } from '../../../../core/services/theme.service';
import { BLACK_THEME, OS_DARK_THEME, OS_LIGHT_THEME } from '../../../../core/constants/theme-options';

/** The two luminance classes the switch chooses between. */
export type PaperMode = 'light' | 'dark';

/** `defaultForMode` from the Ricoh Paper catalogue. Last resort only. */
export const DEFAULT_PAPER: Readonly<Record<PaperMode, ThemeName>> = {
  light: OS_LIGHT_THEME,
  dark: OS_DARK_THEME,
};

/**
 * Cross-luminance counterparts — ONLY the pair the catalogue names itself.
 *
 * High contrast is a need rather than a taste, so its light and dark papers
 * are two halves of one decision and the switch must stay inside the pair
 * even if the viewer was last on some other dark paper.
 */
export const PAPER_COUNTERPART: Readonly<Partial<Record<ThemeName, ThemeName>>> = {
  'high-contrast-light': 'high-contrast-dark',
  'high-contrast-dark': 'high-contrast-light',
};

/** The paper the viewer was last observed on at each luminance. `null` = not
 *  known, which is NOT the same as "the default" and is why this is nullable. */
export type LastSeenPapers = Readonly<Record<PaperMode, ThemeName | null>>;

/**
 * Which paper to write when the switch is flipped to `to`.
 *
 * Precedence: the catalogue's own counterpart, then the paper last seen at
 * that luminance, then the mode default.
 */
export function paperForMode(
  from: ThemeName,
  to: PaperMode,
  lastSeen: LastSeenPapers,
): ThemeName {
  return PAPER_COUNTERPART[from] ?? lastSeen[to] ?? DEFAULT_PAPER[to];
}

/**
 * The three positions of the profile panel's appearance switch.
 *
 * Light is a LUMINANCE (it returns to whichever light paper the viewer had,
 * via `paperForMode`); Graphite and Black are PAPERS — graphite, the default
 * dark, and workplace-slate, the near-black one. Naming two dark papers is
 * what Nath asked for; the other dark papers live on the full shelf.
 */
export type AppearanceSwitch = 'light' | 'graphite' | 'black';

/**
 * Which position reads as selected, or `''` for none.
 *
 * NONE ON ANY OTHER DARK PAPER. A viewer on midnight, Paper's `black` sheet
 * or high-contrast dark is on neither Graphite nor Black, and lighting one
 * of them would claim a paper they are not on — the next tap on it would
 * look like a no-op and silently change their paper. Every light paper reads
 * as Light, because Light means luminance.
 */
export function switchValueFor(theme: ThemeName, isDark: boolean): AppearanceSwitch | '' {
  if (!isDark) return 'light';
  if (theme === OS_DARK_THEME) return 'graphite';
  if (theme === BLACK_THEME) return 'black';
  return '';
}

/** The paper to write when the switch is moved to `to`. */
export function paperForSwitch(
  from: ThemeName,
  to: AppearanceSwitch,
  lastSeen: LastSeenPapers,
): ThemeName {
  if (to === 'graphite') return OS_DARK_THEME;
  if (to === 'black') return BLACK_THEME;
  return paperForMode(from, 'light', lastSeen);
}
